import { ApiError, api } from "@/lib/api";
import type { RelinkSession, RelinkState } from "@/lib/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const TERMINAL: RelinkState[] = ["done", "failed", "cancelled", "expired"];

/** True while a relink is in flight (a session has been started but is not yet
 *  terminal). The wizard uses this to block accidental dialog dismissal
 *  (backdrop click / Escape) so a stray click can't orphan the session on the
 *  backend and lose the pairing code — the exact way S3_1P got stuck (IR-1199).
 *  Before a session starts (intro pane) and once it's terminal, dismissal is
 *  allowed. */
export function shouldBlockDismiss(
  sessionId: string | null,
  sessionState: RelinkState | undefined,
): boolean {
  if (sessionId === null) return false;
  return !(sessionState != null && TERMINAL.includes(sessionState));
}

/** Map a failed start/onboard mutation into operator-facing text. Without this
 *  the wizard swallowed the rejection (no onError) and the operator saw nothing
 *  happen — the S3_1P incident. Returns null when there is no error. */
export function startErrorText(error: unknown): string | null {
  if (!error) return null;
  if (error instanceof ApiError) {
    const detail =
      error.body && typeof error.body === "object" && "error" in error.body
        ? String((error.body as { error: unknown }).error)
        : "";
    if (error.status === 409) {
      return "A relink is already in progress for this avatar. Wait for it to finish, or try again in a minute.";
    }
    return detail || error.message || `Relink could not start (HTTP ${error.status}).`;
  }
  return error instanceof Error ? error.message : "Relink could not start.";
}

export function useRelinkSession(id: string | null) {
  return useQuery({
    queryKey: ["relink", id],
    queryFn: () => api<RelinkSession>(`/api/ops/v1/relink/${id}`),
    enabled: id !== null,
    refetchInterval: (query) => {
      const data = query.state.data as RelinkSession | undefined;
      if (!data || TERMINAL.includes(data.state)) return false;
      return 1000;
    },
  });
}

export function useStartRelink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (avatar_id: number) =>
      api<RelinkSession>("/api/ops/v1/relink/start", {
        method: "POST",
        body: JSON.stringify({ avatar_id }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fleet", "avatars"] }),
  });
}

export function useSubmitInput(sessionId: string) {
  return useMutation({
    mutationFn: (value: string) =>
      api<RelinkSession>(`/api/ops/v1/relink/${sessionId}/input`, {
        method: "POST",
        body: JSON.stringify({ value }),
      }),
  });
}

export function useCancelRelink(sessionId: string) {
  return useMutation({
    mutationFn: () =>
      api<RelinkSession>(`/api/ops/v1/relink/${sessionId}/cancel`, { method: "POST" }),
  });
}
